import React from 'react';
import { Link, LinkProps, useNavigate } from 'react-router-dom';

/**
 * ViewTransitionLink - A Link component with View Transitions API support
 *
 * This component wraps React Router's Link with the viewTransition prop enabled,
 * providing smooth page transitions when navigating between routes.
 *
 * For browsers that don't support the View Transitions API, it falls back
 * to standard React Router navigation without animation.
 */
interface ViewTransitionLinkProps extends Omit<LinkProps, 'viewTransition'> {
  /** Whether to enable view transitions (defaults to true) */
  enableTransition?: boolean;
}

export const ViewTransitionLink = React.forwardRef<HTMLAnchorElement, ViewTransitionLinkProps>(
  ({ enableTransition = true, ...props }, ref) => {
    return (
      <Link
        ref={ref}
        viewTransition={enableTransition}
        {...props}
      />
    );
  }
);

ViewTransitionLink.displayName = 'ViewTransitionLink';

/**
 * useViewTransitionNavigate - Hook for programmatic navigation with view transitions
 *
 * Returns a navigate function that automatically enables view transitions.
 */
export function useViewTransitionNavigate() {
  const navigate = useNavigate();

  return React.useCallback(
    (to: string, options?: { replace?: boolean; state?: unknown }) => {
      navigate(to, {
        ...options,
        viewTransition: true,
      });
    },
    [navigate]
  );
}

export default ViewTransitionLink;
